import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DownloadSimple, Upload, FileXls, CheckCircle, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ImportCompaniesDialog({ open, onOpenChange, onImported }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/companies/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "inventya-companies-import-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    const ok = /\.(xlsx|csv)$/i.test(f.name);
    if (!ok) {
      toast.error("Please pick an .xlsx or .csv file");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/companies/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.imported_count > 0) {
        toast.success(`Imported ${r.data.imported_count} ${r.data.imported_count === 1 ? "company" : "companies"}`);
        onImported?.();
      }
      if (r.data.error_count > 0 && r.data.imported_count === 0) {
        toast.error(`Import failed — ${r.data.error_count} error(s). See details below.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = (o) => {
    if (!o) { reset(); }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import companies from Excel</DialogTitle>
        </DialogHeader>

        {/* Step 1 — template */}
        <div className="rounded-xl border border-border p-5 bg-secondary/30">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 grid place-items-center shrink-0">
              <FileXls size={20} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-sm">Step 1 — download the template</div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Full column set covering every Inventya field — company basics, fundraising, metrics, client access
                and up to <b>3 founders</b>. <b>Row 2 contains a sample row</b> to show the format; <b>row 3 explains each field.</b>
                Delete rows 2–3 before adding your real data, or leave them — they&apos;ll be auto-skipped on import.
              </p>
            </div>
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <DownloadSimple size={16} className="mr-1.5" /> Download template
            </Button>
          </div>
        </div>

        {/* Step 2 — upload */}
        <div className="mt-4 rounded-xl border-2 border-dashed border-border hover:border-emerald-400 dark:hover:border-emerald-700 p-6 transition-colors">
          <div className="text-center">
            <div className="mx-auto h-10 w-10 rounded-full bg-secondary grid place-items-center mb-3">
              <Upload size={20} />
            </div>
            <div className="font-semibold text-sm mb-1">Step 2 — upload your completed file</div>
            <p className="text-xs text-muted-foreground mb-4">Accepts .xlsx or .csv</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="import-file-input"
            />
            {!file ? (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                data-testid="choose-file-btn"
              >
                <Upload size={16} className="mr-1.5" /> Choose file
              </Button>
            ) : (
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-sm">
                <FileXls size={16} weight="fill" />
                <span className="font-medium">{file.name}</span>
                <button onClick={reset} className="opacity-60 hover:opacity-100 ml-1"><ArrowClockwise size={13} /></button>
              </div>
            )}
          </div>
        </div>

        {/* Result panel */}
        {result && (
          <div className="mt-4 space-y-3" data-testid="import-result">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 dark:text-emerald-300">Imported</div>
                <div className="mt-1 flex items-center gap-2">
                  <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                  <div className="text-2xl font-display font-bold text-emerald-900 dark:text-emerald-200 num-display">{result.imported_count}</div>
                </div>
              </div>
              <div className={`rounded-lg border p-3 ${result.error_count > 0 ? "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30" : "border-border bg-secondary/30"}`}>
                <div className={`text-[10px] uppercase tracking-wider font-bold ${result.error_count > 0 ? "text-red-800 dark:text-red-300" : "text-muted-foreground"}`}>Errors</div>
                <div className="mt-1 flex items-center gap-2">
                  <WarningCircle size={20} weight={result.error_count > 0 ? "fill" : "regular"} className={result.error_count > 0 ? "text-red-600" : "text-muted-foreground"} />
                  <div className={`text-2xl font-display font-bold num-display ${result.error_count > 0 ? "text-red-900 dark:text-red-200" : ""}`}>{result.error_count}</div>
                </div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-lg border border-red-200 dark:border-red-900 max-h-64 overflow-auto" data-testid="import-errors-list">
                <div className="px-3 py-2 bg-red-100/60 dark:bg-red-950/40 text-[10px] uppercase tracking-wider font-bold text-red-800 dark:text-red-300 flex items-center gap-2 sticky top-0">
                  <WarningCircle size={12} weight="fill" /> {result.errors.length} row(s) could not be imported
                </div>
                <div className="divide-y divide-red-100 dark:divide-red-950">
                  {result.errors.map((e, i) => (
                    <div key={i} className="px-3 py-2.5 text-xs" data-testid={`import-error-row-${e.row}`}>
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-red-700 dark:text-red-400 shrink-0">Row {e.row}</span>
                        <span className="font-semibold truncate">{e.name || <span className="italic text-muted-foreground">(no name)</span>}</span>
                      </div>
                      <div className="text-red-700 dark:text-red-400 leading-relaxed whitespace-pre-wrap">{e.error}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => handleClose(false)}>
            {result ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={submit}
            disabled={!file || busy}
            data-testid="import-submit-btn"
            className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900"
          >
            {busy ? "Importing…" : result ? "Import another file" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
